import React from "react";
import styled from "styled-components";
import { withPrefix } from "gatsby";
import DiscussionThread from "./DiscussionThread";
import cleanCompanyName from "../../utilities/cleanCompanyName";
import getEntityHash from "../../utilities/getEntityHash";
import SupplyChainEntityTypeIcon from "../baitToPlate/BaitToPlateEntityTypeIcon";
import { ArrowUpRight } from "../Arrow";
import SupplyChainIndexItemFlags from "../baitToPlate/BaitToPlateIndexItemFlags";
import DiscussionInteractionCount from "./DiscussionInteractionCount";
import BaitToPlateEntityLink from "../baitToPlate/BaitToPlateEntityLink";

function formatDates(startDate, endDate) {
  const monthNames = ["January", "February", "March", "April", "May", "June",
                      "July", "August", "September", "October", "November", "December"];
  
  function parseDate(dateString) {
      const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
      return new Date(year, month - 1, day); // Month is 0-indexed
  }

  const start = parseDate(startDate);
  const end = parseDate(endDate);

  const startMonth = monthNames[start.getMonth()];
  const endMonth = monthNames[end.getMonth()];
  const startDay = start.getDate();
  const endDay = end.getDate();
  const startYear = start.getFullYear();
  const endYear = end.getFullYear();

  if (startDate === endDate) {
      return `${startMonth} ${startDay}, ${startYear}`;
  } else if (startMonth === endMonth && startYear === endYear) {
      return `${startMonth} ${startDay} - ${endDay}, ${startYear}`;
  } else if (startYear === endYear) {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
  } else {
      return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }
}

const DiscussionProfile = ({ entity }) => {
  
  const hasFlags = entity.flags?.length > 0 || entity.processor_flags?.length > 0 || entity.vessel_flags?.length > 0;


  const firstCorrespondenceDate = entity.correspondence[0].date;
  const lastCorrespondenceDate = entity.correspondence[entity.correspondence.length - 1].date;


  return (
    <StyledDiscussionProfile>
      <header className="header">
        <h1>{cleanCompanyName(entity.name)}</h1>
        <div className="meta">
          <div className="country">{entity.country.name}</div>
          {entity.website !== "" && (
            <div className="website">
              <a href={entity.website} title="Go to website" target="_blank" rel="noopener noreferrer"><span className="label">Website</span> <span className="arrow"><ArrowUpRight /></span></a>
            </div>
          )}
        </div>
      </header>
      {(hasFlags || entity.supplyChainMatches?.length > 0 || entity.ubiquitySupplyChainMatches?.length > 0 ) && (
        <div className="meta">
          {hasFlags && (
            <>
              <h4>Summary of Crimes & Concerns</h4>
              <SupplyChainIndexItemFlags item={entity} asSummary={true} />
            </>
          )}
          {entity.supplyChainMatches?.length > 0 && (
            <>
              <h4>Bait-to-Plate Profile{entity.supplyChainMatches.length > 1 && "s"}</h4>
              <div className="b2p-links">
                {entity.supplyChainMatches.map((match, index) => (
                  <BaitToPlateEntityLink key={index} entity={match} inNewWindow={true} className="b2p-link">
                    <span className="icon"><SupplyChainEntityTypeIcon type={match.type} /></span>
                    <span className="label">{cleanCompanyName(match.name)}</span>
                  </BaitToPlateEntityLink>
                ))}
              </div>
            </>
          )}
          {/*entity.supplyChainMatches?.length < 1 && entity.ubiquitySupplyChainMatches?.length > 0 && (
            <>
              <h4>Ubiquity Profile{entity.ubiquitySupplyChainMatches.length > 1 && "s"}</h4>
              <div className="b2p-links">
                {entity.ubiquitySupplyChainMatches.map((match, index) => (
                  <a className="b2p-link" href={withPrefix(`/ubiquity/${getEntityHash(match)}`)} key={index} target="_blank" rel="noopener noreferrer">
                    <span className="icon"><SupplyChainEntityTypeIcon type={match.type} /></span>
                    <span className="label">{cleanCompanyName(match.name)}</span> 
                  </a>
                ))}
              </div>
            </>
                )*/}
        </div>
      )}
      <div className="content">
        <div className="section">
          <StyledCorrespondenceMeta>
            <h3 className="section-heading">Correspondence</h3>
            <div className="interactions">
              <div className="dates">{formatDates(firstCorrespondenceDate, lastCorrespondenceDate)}</div>
              <DiscussionInteractionCount inquiries={entity.count_inquiries} replies={entity.count_replies} />
            </div>
          </StyledCorrespondenceMeta>
          <DiscussionThread entity={entity} />
        </div>
      </div>
    </StyledDiscussionProfile>
  );
};

export default DiscussionProfile;

const StyledCorrespondenceMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 1.5em;
  .interactions {
    display: flex;
    gap: 1em;
    .dates {
      font-size: 0.875em;
      opacity: 0.6;
      display: none;
    }
  }
`

const StyledDiscussionProfile = styled.div`
  padding: 3em 0;
  @media ( min-width: 70em ) {
    display: grid;
    grid-template: auto / 1fr 15em;
    align-items: start;
    gap: 2em;
    .header {
      grid-column: 1 / span 2;
    }
    > .meta {
      position: sticky;
      grid-column: 2;
      top: calc((var(--navbar-height) + 77px) + 1.5em);
    }

    .content {
      grid-column: 1;
      grid-row: 2;
    }
    .content .section:first-of-type {
      margin-top: 0;
    }
  }
  .header {
    display: flex;
    flex-direction: column;
    gap: 0.75em;
    border-bottom: 1px solid rgba(255,255,255,0.2);
    padding-bottom: 1.5em;
    margin-bottom: 2em;
    @media ( min-width: 70em ) {
      margin-bottom: 0;
      border-bottom: 0;
      padding-bottom: 0;
    }
    h1 {
      font-weight: 700;
      font-size: 1.375em;
      line-height: 1.2;
      margin: 0;
    }
    h2 {
      font-size: 0.875em;
      font-weight: 700;
      margin: 0;
      line-height: 1;
      opacity: 0.5;
    }
    @media ( min-width: 48em ) {
      h1 {
        font-size: 1.5em;
      }
    }
    @media ( min-width: 60em ) {
      h1 {
        font-size: 2em;
      }
    }

    .meta {
      display: flex;
      gap: 1em;
      font-size: 0.875em;
      opacity: 0.6;
      @media ( min-width: 48em ) { 
        font-size: 1em;
      }
      .website {
        display: flex;
        align-items: baseline;
        gap: 0.25em;
        .label {
          text-decoration-line: underline;
          text-decoration-thickness: 0.75px;
          text-underline-offset: 0.1em;
          text-decoration-color: rgba(255,255,255,0.4);
        }
        .arrow {
          font-size: 0.75em;
          display: inline-block;
          transform: translate3d(0,0.125em,0);
        }

        .can-hover &:hover,
        &:active {
          .label {
            text-decoration-color: currentColor;
          }
        }
      }
    }
  }
  @media ( min-width: 80em ) {
    grid-template: auto / 1fr 19em;
  }

  > .meta {
    background-color: #191919;
    padding: 1.5em;
    border-radius: 1em;
    h4 {
      font-weight: 700;
      line-height: 1.3;
      margin: 0 0 0.75em;
      &:not(:first-of-type) {
        margin-top: 1.25em;
        border-top: 1px solid rgba(255,255,255,0.2);
        padding-top: 1em;
      }
    }
  }

  .section {
    position: relative;
    margin-top: 2em;
    padding-top: 2em;
    display: flex;
    flex-direction: column;
    gap: 1.25em;
    @media ( min-width: 48em ) {
      margin-top: 3em;
      gap: 1.5em;
    }
    &::before {
      content: "";
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      border-top: 1px solid;
      opacity: 0.2;
    }
    .section-heading {
      font-weight: 700;
      font-size: 1.125em;
      margin: 0;
      line-height: 1;
    }
    h4 {
      font-weight: 700;
      font-size: 0.9375em;
      margin: 0 0 0.5em;
    }
    @media ( min-width: 48em ) {
      .section-heading {
        font-size: 1.25em;
      }
      h4 {
        font-size: 1em;
      }
    }
  }

  .bait-to-plate {

  }

  .b2p-links {
    display: flex;
    flex-direction: column;
    gap: 0.75em;
  }

  .b2p-link {
    position: relative;
    display: grid;
    grid-template: auto / 2.25em 1fr;
    align-items: center;
    gap: 0.75em;
    .icon {
      position: relative;
      transition: transform 0.3s ease, opacity 0.3s ease;
      cursor: pointer;
      &::before {
        content: "";
        display: block;
        padding-bottom: 100%;
      }
      background-color: var(--color-white);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--color-black);
      z-index: 0;
      width: 100%;
      padding: 0.625em;
      width: 2.25em;
      padding: 0.25em;
      &.is-importer {
        padding: 0.375em;
      }
    }
    .label {
      font-weight: 500;
      line-height: 1.2;

      text-decoration-line: underline;
      text-decoration-thickness: 0.75px;
      text-underline-offset: 0.1em;
      text-decoration-color: rgba(255,255,255,0.4);
    }
    .can-hover &:hover,
    &:active {
      .label {
        text-decoration-color: currentColor;
      }
    }
  }
`;
